import React, {useState} from 'react';
import {Pressable, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {F, S, T} from '../courseTheme';
import {E} from '../editor/editorTheme';
import {focusVisible, motion, ring} from '../editor/ui';
import PuzzleIcon from './PuzzleIcon';
import {prefersReducedMotion} from './prefersReducedMotion';

type Props = {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
};

/** The spec's one line on what these courses are (2026-10-04 design, §Frontend). */
export const ACTIVE_PUZZLES_EXPLAINER =
  'Built from the puzzles the coach launched in class — exact positions, ' +
  'the coach’s own solutions and the real launch times. No reading boards off the video.';

const TRACK_W = 40;
const TRACK_H = 24;
const THUMB = 18;

/**
 * "Active puzzles only": the switch above the recording picker, and the one
 * line that says what it means.
 *
 * A real switch to assistive tech (role + checked), with the whole panel as
 * its target. The thumb's position says on/off as well as the track's
 * colour, so the state never rests on colour alone; the off track (#8A8785)
 * is 3.6:1 against white and the on track (tealDark) 5.2:1, both above the
 * 3:1 a control boundary needs.
 */
export default function ActivePuzzlesToggle({
  value,
  onChange,
  disabled = false,
}: Props) {
  const [focused, setFocused] = useState(false);
  const [reduced] = useState(prefersReducedMotion);

  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityLabel="Active puzzles only"
      accessibilityHint="Lists only recordings where the coach launched puzzles in class"
      accessibilityState={{checked: value, disabled}}
      // react-native-web 0.19 drops accessibilityState; only aria-* reaches
      // the DOM.
      {...({'aria-checked': value} as any)}
      disabled={disabled}
      onPress={() => {
        if (!disabled) onChange(!value);
      }}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      // One panel: the switch, its name and what it means read as a single
      // control, so the explainer never floats loose under the switch. On,
      // the panel takes the selected tint, so the mode shows from across
      // the card and not only in the thumb.
      style={({pressed, hovered}: any) => [
        {
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: S[3],
          padding: S[3],
          borderRadius: 12,
          borderWidth: 1,
          borderColor: value ? F.tealDark : E.line,
          backgroundColor: value
            ? E.selected
            : pressed
            ? E.pressed
            : hovered
            ? E.hover
            : F.white,
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        } as any,
        motion(reduced),
        ring(focused),
      ]}>
      <View
        style={[
          {
            width: TRACK_W,
            height: TRACK_H,
            borderRadius: TRACK_H / 2,
            backgroundColor: value ? F.tealDark : E.inputLine,
            justifyContent: 'center',
            paddingHorizontal: (TRACK_H - THUMB) / 2,
          },
          motion(reduced),
        ]}>
        <View
          style={{
            width: THUMB,
            height: THUMB,
            borderRadius: THUMB / 2,
            backgroundColor: F.white,
            alignSelf: value ? 'flex-end' : 'flex-start',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {value ? (
            <Ionicons name="checkmark" size={12} color={F.tealDark} />
          ) : null}
        </View>
      </View>
      <View style={{flex: 1, gap: 2}}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: S[2],
            minHeight: TRACK_H,
          }}>
          <PuzzleIcon size={16} color={F.tealDark} />
          <CCText
            style={{fontSize: T.base, fontWeight: '600', color: F.brownDark}}>
            Active puzzles only
          </CCText>
        </View>
        <CCText
          style={{
            fontSize: T.sm,
            fontWeight: '500',
            color: E.muted,
            lineHeight: 19,
          }}>
          {ACTIVE_PUZZLES_EXPLAINER}
        </CCText>
      </View>
    </Pressable>
  );
}
